/**
 * WESN Engraving Customizer
 * Handles the full-screen engraving overlay, live preview,
 * variant syncing, and cart submission.
 */
(function () {
    'use strict';

    const CONFIG = window.WESN_ENGRAVING_CONFIG;
    if (!CONFIG) return;

    /* ---- DOM refs ---- */
    const trigger = document.querySelector('[data-engraving-trigger]');
    const overlay = document.getElementById('engraving-customizer');
    if (!trigger || !overlay) return;

    // Move overlay to <body> so it escapes any parent stacking contexts
    // (e.g. the product section) and always sits above the site header.
    document.body.appendChild(overlay);

    const els = {
        triggerBtn: trigger.querySelector('.engraving-trigger__button'),
        close: overlay.querySelector('[data-engrave-close]'),
        previewImg: overlay.querySelector('[data-engrave-preview-img]'),
        previewText: overlay.querySelector('[data-engrave-preview-text]'),
        input: overlay.querySelector('[data-engrave-input]'),
        charCurrent: overlay.querySelector('[data-engrave-char-current]'),
        charCount: overlay.querySelector('[data-engrave-char-count]'),
        toReview: overlay.querySelector('[data-engrave-to-review]'),
        addToCart: overlay.querySelector('[data-engrave-add-to-cart]'),
        backToEditor: overlay.querySelector('[data-engrave-back-to-editor]'),
        stepEditor: overlay.querySelector('[data-engrave-step="editor"]'),
        stepReview: overlay.querySelector('[data-engrave-step="review"]'),
        reviewText: overlay.querySelector('[data-engrave-review-text]'),
        reviewVariant: overlay.querySelector('[data-engrave-review-variant]'),
        reviewProductPrice: overlay.querySelector('[data-engrave-review-product-price]'),
        reviewFee: overlay.querySelector('[data-engrave-review-fee]'),
        reviewTotal: overlay.querySelector('[data-engrave-review-total]'),
        quitModal: overlay.querySelector('[data-engrave-quit-modal]'),
        quitCancel: overlay.querySelectorAll('[data-engrave-quit-cancel]'),
        quitConfirm: overlay.querySelector('[data-engrave-quit-confirm]'),
        backToPreview: overlay.querySelector('[data-engrave-back-to-preview]')
    };

    /* ---- State ---- */
    let state = {
        text: '',
        variantId: CONFIG.currentVariantId,
        open: false,
        step: 'editor' // 'editor' | 'review'
    };

    /* ---- Preview images from trigger data attribute ---- */
    let previewImages = {};
    try {
        previewImages = JSON.parse(trigger.dataset.previewImages || '{}');
    } catch (e) {
        previewImages = {};
    }

    /* ---- Helpers ---- */

    function formatMoney(cents) {
        // Always display in USD regardless of visitor location
        const amount = (cents / 100).toFixed(2);
        return '$' + amount;
    }

    function getVariant(id) {
        return CONFIG.variants.find(v => v.id === id);
    }

    function getCurrentVariantFromPage() {
        // Try to get the currently selected variant from the PDP
        // Common patterns: URL param, hidden input, or select element
        const urlParams = new URLSearchParams(window.location.search);
        const urlVariant = urlParams.get('variant');
        if (urlVariant) return parseInt(urlVariant, 10);

        const hiddenInput = document.querySelector('input[name="id"]');
        if (hiddenInput) return parseInt(hiddenInput.value, 10);

        return CONFIG.currentVariantId;
    }

    function normalizeColorKey(str) {
        return str.toLowerCase().trim().replace(/\s+/g, '-');
    }

    function getPreviewImageForVariant(variantId) {
        const variant = getVariant(variantId);
        if (!variant) return null;

        // Try to match variant option to a preview image key
        for (const option of variant.options) {
            const key = normalizeColorKey(option);
            if (previewImages[key]) return previewImages[key];
        }

        // Fall back to default preview image
        return previewImages['_default'] || null;
    }

    function updatePreviewImage() {
        const src = getPreviewImageForVariant(state.variantId);
        if (src) {
            els.previewImg.src = src;
        }
    }

    /* ---- Open / Close ---- */

    function open() {
        state.open = true;
        state.text = '';
        state.step = 'editor';
        state.variantId = getCurrentVariantFromPage();

        els.input.value = '';
        els.charCurrent.textContent = '0';
        els.charCount.classList.remove('is-near-limit', 'is-at-limit');
        els.previewText.textContent = '';
        els.previewText.classList.remove('is-visible');
        els.toReview.disabled = true;

        showStep('editor');
        updatePreviewImage();

        overlay.setAttribute('aria-hidden', 'false');
        document.body.classList.add('engrave-open');

        // Focus the input after transition
        setTimeout(() => els.input.focus(), 350);
    }

    function close() {
        if (state.text.length > 0) {
            showQuitModal();
            return;
        }
        forceClose();
    }

    function forceClose() {
        state.open = false;
        overlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('engrave-open');
        hideQuitModal();
    }

    /* ---- Steps ---- */

    function showStep(step) {
        state.step = step;
        if (step === 'editor') {
            els.stepEditor.hidden = false;
            els.stepReview.hidden = true;
        } else {
            els.stepEditor.hidden = true;
            els.stepReview.hidden = false;
            populateReview();
        }
    }

    function populateReview() {
        const variant = getVariant(state.variantId);
        const productPrice = variant ? variant.price : 0;
        const engravingFee = CONFIG.isFree ? 0 : CONFIG.price;
        const total = productPrice + engravingFee;

        els.reviewText.textContent = state.text;
        els.reviewVariant.textContent = variant ? variant.title : '';
        els.reviewProductPrice.textContent = formatMoney(productPrice);
        els.reviewFee.textContent = CONFIG.isFree ? 'FREE' : formatMoney(engravingFee);
        els.reviewTotal.textContent = formatMoney(total);
    }

    /* ---- Quit modal ---- */

    function showQuitModal() {
        els.quitModal.setAttribute('aria-hidden', 'false');
    }

    function hideQuitModal() {
        els.quitModal.setAttribute('aria-hidden', 'true');
    }

    /* ---- Live preview ---- */

    function updateLivePreview() {
        const text = state.text;
        els.previewText.textContent = text;

        if (text.length > 0) {
            els.previewText.classList.add('is-visible');
        } else {
            els.previewText.classList.remove('is-visible');
        }

    }

    /* ---- Input handling ---- */

    function handleInput() {
        const raw = els.input.value;
        state.text = raw;
        const len = raw.length;

        els.charCurrent.textContent = len;

        // Character count styling
        els.charCount.classList.remove('is-near-limit', 'is-at-limit');
        if (len >= CONFIG.maxChars) {
            els.charCount.classList.add('is-at-limit');
        } else if (len >= CONFIG.maxChars * 0.8) {
            els.charCount.classList.add('is-near-limit');
        }

        // Enable/disable review button
        els.toReview.disabled = len < CONFIG.minChars;

        // Update live preview
        updateLivePreview();
    }

    /* ---- Add to cart ---- */

    async function addToCart(withEngraving) {
        const variant = getVariant(state.variantId);
        if (!variant || !variant.available) {
            alert('This variant is currently unavailable.');
            return;
        }

        const properties = {};
        const shouldEngrave = withEngraving && state.text.length >= CONFIG.minChars;

        if (shouldEngrave) {
            // Visible properties (shown in cart to customer)
            properties['Engraving'] = state.text;

            // Hidden properties (prefixed with _ for Shopify)
            properties['_engraving_text'] = state.text;
            properties['_non_refundable'] = 'true';
            properties['_extra_shipping_days'] = String(CONFIG.extraDays);
        }

        els.addToCart && els.addToCart.classList.add('is-loading');
        els.addToCart && (els.addToCart.textContent = 'Adding...');

        try {
            // Build items array - product + optional engraving fee product
            const items = [
                {
                    id: state.variantId,
                    quantity: 1,
                    properties: properties
                }
            ];

            // Add the engraving fee as a separate product (line item properties don't affect price)
            if (shouldEngrave && !CONFIG.isFree && CONFIG.feeVariantId) {
                items.push({
                    id: CONFIG.feeVariantId,
                    quantity: 1,
                    properties: {
                        '_engraving_for': CONFIG.productTitle,
                        '_engraving_for_variant': String(state.variantId)
                    }
                });
            }

            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: items })
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.description || 'Could not add to cart');
            }

            // Success - reset button state and close customizer
            els.addToCart && els.addToCart.classList.remove('is-loading');
            els.addToCart && (els.addToCart.textContent = 'Add to Cart');
            forceClose();

            // Trigger cart update event for theme integration
            document.dispatchEvent(new CustomEvent('cart:item-added', {
                detail: { variantId: state.variantId, engraving: state.text }
            }));

            // Refresh the cart drawer (don't redirect to cart page)
            // Try common theme cart drawer patterns
            if (typeof window.cart !== 'undefined' && typeof window.cart.open === 'function') {
                window.cart.open();
            } else if (typeof window.refreshCart === 'function') {
                window.refreshCart();
            } else {
                // Dispatch a generic event that many themes listen for
                document.dispatchEvent(new CustomEvent('cart:refresh'));
                // Try clicking the cart icon to open the drawer
                const cartIcon = document.querySelector('[data-cart-drawer], .cart-link, .header-cart, [href="/cart"]');
                if (cartIcon) cartIcon.click();
            }

        } catch (err) {
            alert(err.message || 'Something went wrong. Please try again.');
            els.addToCart && els.addToCart.classList.remove('is-loading');
            els.addToCart && (els.addToCart.textContent = 'Add to Cart');
        }
    }

    /* ---- Variant sync ---- */

    function syncVariant() {
        const newVariantId = getCurrentVariantFromPage();
        if (newVariantId && newVariantId !== state.variantId) {
            state.variantId = newVariantId;
            if (state.open) {
                updatePreviewImage();
                if (state.step === 'review') {
                    populateReview();
                }
            }
        }
    }

    /* ---- Event listeners ---- */

    // Open customizer
    els.triggerBtn.addEventListener('click', open);

    // Close
    els.close.addEventListener('click', close);

    // Keyboard - Escape to close
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && state.open) {
            e.preventDefault();
            close();
        }
    });

    // Text input
    els.input.addEventListener('input', handleInput);

    // Prevent form submission on Enter
    els.input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (!els.toReview.disabled) {
                showStep('review');
            }
        }
    });

    // Review step
    els.toReview.addEventListener('click', function () {
        showStep('review');
    });

    // Back to editor
    els.backToEditor.addEventListener('click', function () {
        showStep('editor');
        setTimeout(() => els.input.focus(), 100);
    });

    // Add to cart (with engraving)
    els.addToCart.addEventListener('click', function () {
        addToCart(true);
    });

    // Gallery: clicking a thumbnail expands it in the main preview frame
    // (without the live text overlay — gallery items are just reference photos)
    const galleryItems = overlay.querySelectorAll('[data-engrave-gallery-item]');
    const originalPreviewSrc = els.previewImg.src;

    function returnToPreview() {
        galleryItems.forEach(function (g) { g.classList.remove('is-active'); });
        els.previewImg.src = originalPreviewSrc;
        els.previewText.style.display = '';
        if (els.backToPreview) els.backToPreview.classList.remove('is-visible');
    }

    galleryItems.forEach(function (item) {
        item.addEventListener('click', function () {
            const isActive = item.classList.contains('is-active');

            // Remove active from all
            galleryItems.forEach(function (g) { g.classList.remove('is-active'); });

            if (isActive) {
                returnToPreview();
            } else {
                // Show this gallery image, hide the live text overlay
                item.classList.add('is-active');
                var fullSrc = item.getAttribute('data-full-src');
                if (fullSrc) {
                    els.previewImg.src = fullSrc;
                } else {
                    const img = item.querySelector('img');
                    if (img) els.previewImg.src = img.src;
                }
                els.previewText.style.display = 'none';
                if (els.backToPreview) els.backToPreview.classList.add('is-visible');
            }
        });
    });

    // Back to preview button
    if (els.backToPreview) {
        els.backToPreview.addEventListener('click', returnToPreview);
    }

    // Quit modal
    els.quitCancel.forEach(function (el) {
        el.addEventListener('click', hideQuitModal);
    });

    els.quitConfirm.addEventListener('click', forceClose);

    // Listen for variant changes on the PDP
    // Support common theme patterns
    document.addEventListener('variant:changed', function (e) {
        if (e.detail && e.detail.variant) {
            state.variantId = e.detail.variant.id;
            if (state.open) {
                updatePreviewImage();
            }
        }
    });

    // Also poll for URL variant changes (fallback)
    let lastUrl = window.location.href;
    const urlObserver = new MutationObserver(function () {
        if (window.location.href !== lastUrl) {
            lastUrl = window.location.href;
            syncVariant();
        }
    });
    urlObserver.observe(document.querySelector('body'), { childList: true, subtree: true });

    // Also listen for the hidden variant input changing
    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput) {
        const inputObserver = new MutationObserver(function () {
            syncVariant();
        });
        inputObserver.observe(variantInput, { attributes: true, attributeFilter: ['value'] });

        // Some themes update via JS property, not attribute
        let currentVal = variantInput.value;
        setInterval(function () {
            if (variantInput.value !== currentVal) {
                currentVal = variantInput.value;
                syncVariant();
            }
        }, 500);
    }

})();